import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Users } from 'lucide-react'
import { usePresence } from '@/contexts/PresenceContext'
import { useUser } from '@/app/store/auth'
import { formatTime } from '@/lib/dateUtils'
import { bindRunbookViewers, otherViewers, type RunbookViewer } from '../runbook-viewers'

/**
 * "Someone else is working on this runbook now."
 *
 * Shown to everyone who has the runbook open while another person has it open too, so the
 * engineer already working sees the second arrive as well. Amber when the other person can
 * change it; a quiet line when they are only reading. Nothing is blocked.
 */
export function RunbookViewersNotice({ requestID, readOnly }: { requestID: number | string; readOnly: boolean }) {
    const { t } = useTranslation()
    const { socket } = usePresence()
    const user = useUser()
    const [viewers, setViewers] = useState<RunbookViewer[]>([])

    useEffect(() => {
        if (!socket || requestID === '' || requestID == null) return
        return bindRunbookViewers(socket, requestID, !readOnly, setViewers)
    }, [socket, requestID, readOnly])

    const { working, reading } = otherViewers(viewers, user?.id)
    if (working.length === 0 && reading.length === 0) return null
    const nameOf = (v: RunbookViewer) => v.name || t('recoverRequests.runbookViewersSomeone', 'Another user')

    if (working.length === 0) {
        return (
            <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="runbook-viewers-reading">
                <Users className="h-3.5 w-3.5 flex-shrink-0" />
                {t('recoverRequests.runbookViewersReading', 'Also viewing: {{names}}', { names: reading.map(nameOf).join(', ') })}
            </p>
        )
    }
    return (
        <div
            role="status"
            data-testid="runbook-viewers-working"
            className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-700 dark:text-amber-400"
        >
            <Users className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
                <p className="font-medium">
                    {working.length === 1
                        ? t('recoverRequests.runbookViewersWorkingOne', '{{name}} is working on this runbook now (opened {{time}}).', { name: nameOf(working[0]), time: formatTime(working[0].since) })
                        : t('recoverRequests.runbookViewersWorkingMany', '{{names}} are working on this runbook now.', { names: working.map(nameOf).join(', ') })}
                </p>
                {!readOnly && (
                    <p className="mt-0.5">{t('recoverRequests.runbookViewersOverwrite', 'Changes you each save can overwrite the other\'s. Agree who is updating it before you change anything.')}</p>
                )}
                {reading.length > 0 && (
                    <p className="mt-0.5 text-xs">{t('recoverRequests.runbookViewersReading', 'Also viewing: {{names}}', { names: reading.map(nameOf).join(', ') })}</p>
                )}
            </div>
        </div>
    )
}
